import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Board } from 'src/app/core/models/board.model';
import { BoardActivation } from 'src/app/core/models/boardActivation.model';
import { BoardService } from 'src/app/core/services/board.service';
import { boardActivationService } from 'src/app/core/services/boardActivation.service';
import { LoginService } from 'src/app/core/services/login.service';
import { Subject, Observable, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-activation',
  templateUrl: './activation.component.html',
  styleUrls: ['./activation.component.scss']
})
export class ActivationComponent implements OnInit {
  term: string = '';
  private searchSubject = new Subject<string>();
  boardActivation: BoardActivation[] = [];
  board: Board[] = [];
  filtetedActivations: BoardActivation[] = [];
  filteredPagenatedActivations: BoardActivation[];
  private searchCache = new Map<string, BoardActivation[]>();
  private searchIndex = new Map<string, Set<BoardActivation>>();
  isLoading = false;
  readonly defaultMonths = 6;
  monthsLoaded = this.defaultMonths;
  readonly = false;
  breadCrumbItems: Array<{}>;

  pageSize = 50;
  pageNumber = 0;
  numberOfPages = 0;

  constructor(
    private boardService: BoardService,
    private boardActivationService: boardActivationService,
    private loginService: LoginService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef
  ) {
    this.setupSearch();
  }

  private isExternalSalesRep(): boolean {
    return this.loginService.userType === 'EXTERNAL';
  }

  private normalizeId(value: any): string {
    return (value || '').toString().replace(/-/g, '').trim().toLowerCase();
  }

  private matchesCurrentSalesRep(item: BoardActivation): boolean {
    if (!this.isExternalSalesRep()) {
      return true;
    }

    const currentRepId = this.normalizeId(this.loginService.userId);
    if (!currentRepId) {
      return false;
    }

    const candidateIds = [
      item?.board?.salesRep?.id,
      item?.board?.location?.salesRep?.id,
      item?.board?.currentDeployment?.location?.salesRep?.id,
    ].map(value => this.normalizeId(value)).filter(Boolean);

    return candidateIds.includes(currentRepId);
  }

  private matchesAllowedBoard(item: BoardActivation, allowedBoardIds: Set<string>, allowedAssetTags: Set<string>, allowedSerials: Set<string>): boolean {
    if (!this.isExternalSalesRep()) {
      return true;
    }

    const boardId = this.normalizeId(item?.board?.id);
    const assetTag = (item?.board?.assetTag || '').toString().trim().toLowerCase();
    const serial = (item?.board?.serialNumber || '').toString().trim().toLowerCase();

    return (boardId && allowedBoardIds.has(boardId))
      || (assetTag && allowedAssetTags.has(assetTag))
      || (serial && allowedSerials.has(serial));
  }

  private setupSearch() {
    this.searchSubject.pipe(
      debounceTime(10),
      distinctUntilChanged(),
      switchMap(term => this.searchAsync(term))
    ).subscribe(results => {
      this.pageNumber = 0;
      this.filtetedActivations = results;
      this.updatePagination();
      this.cdr.detectChanges();
    });
  }

  async ngOnInit(): Promise<void> {
    this.breadCrumbItems = [{ label: 'Fortune' }, { label: 'Activation', active: true }];
    await this.refresh();
  }

  async refresh(): Promise<void> {
    if (this.isLoading) return;
    
    this.isLoading = true;
    
    try {
      const cutoffDate = this.getCutoffDate();
      const cutoffTs = cutoffDate.getTime();
      const externalBoards = this.isExternalSalesRep()
        ? await this.boardService.getBoardsBySalesRepId(this.loginService.userId)
        : [];

      const allowedBoardIds = new Set((externalBoards || []).map(board => this.normalizeId(board?.id)).filter(Boolean));
      const allowedAssetTags = new Set((externalBoards || []).map(board => (board?.assetTag || '').toString().trim().toLowerCase()).filter(Boolean));
      const allowedSerials = new Set((externalBoards || []).map(board => (board?.serialNumber || '').toString().trim().toLowerCase()).filter(Boolean));

      this.boardActivation = await this.boardActivationService.getAll({
        cutoffDate,
        maxPages: 50,
      });
      this.boardActivation = (this.boardActivation || []).filter(item => {
        const ts = Date.parse(item?.timeStamp || '');
        return Number.isFinite(ts) ? ts >= cutoffTs : true;
      });
      this.boardActivation = this.boardActivation.filter(item => {
        if (this.matchesAllowedBoard(item, allowedBoardIds, allowedAssetTags, allowedSerials)) {
          return true;
        }

        return this.matchesCurrentSalesRep(item);
      });
      this.filtetedActivations = this.boardActivation;
      this.searchCache.clear();
      this.buildSearchIndex();
      this.pageNumber = 0;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error refreshing activations:', error);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
    this.updatePagination();
  }

  private buildSearchIndex(): void {
    this.searchIndex.clear();
    
    this.boardActivation.forEach(activation => {
      const searchText = this.getActivationSearchText(activation);
      const tokens = new Set(searchText.split(/\s+/));

      tokens.forEach(token => {
        if (!this.searchIndex.has(token)) {
          this.searchIndex.set(token, new Set<BoardActivation>());
        }
        const tokenSet = this.searchIndex.get(token);
        if (tokenSet) {
          tokenSet.add(activation);
        }
      });
    });
  }

  private getActivationSearchText(activation: BoardActivation): string {
    const board: any = activation?.board || {};
    const searchFields = [
      board.assetTag,
      board.asset_tag,
      board.serialNumber,
      board.serial_number,
      board.location?.locationName,
      board.installedGamePackage?.name,
      activation.activationKey
    ];

    return searchFields
      .filter(value => value !== null && value !== undefined)
      .map(value => value.toString().toLowerCase())
      .join(' ');
  }

  private async searchAsync(term: string): Promise<BoardActivation[]> {
    const searchTerm = term.trim().toLowerCase();
    
    if (!searchTerm) {
      return this.boardActivation;
    }

    // Check cache first
    const cachedResults = this.searchCache.get(searchTerm);
    if (cachedResults) {
      return cachedResults;
    }

    const searchTokens = searchTerm.split(/\s+/);
    const results = this.boardActivation.filter((activation: BoardActivation) => {
      const itemText = this.getActivationSearchText(activation);
      return searchTokens.every(token => itemText.includes(token));
    });

    this.searchCache.set(searchTerm, results);
    return results;
  }

  onSearch(): void {
    this.searchSubject.next(this.term);
  }

  updatePagination(): void {
  const pageStart = this.pageSize * this.pageNumber;
  const pageStop = Math.min(pageStart + this.pageSize, this.filtetedActivations.length);
  this.filteredPagenatedActivations = this.filtetedActivations.slice(pageStart, pageStop);
  this.numberOfPages = Math.ceil(this.filtetedActivations.length / this.pageSize);
}

canIncrement(): boolean {
  return this.pageNumber < this.numberOfPages - 1;
}

incrementPage(): void {
  if (this.canIncrement()) {
    this.pageNumber++;
    this.updatePagination();
  }
}

canDecrement(): boolean {
  return this.pageNumber > 0;
}

  decrementPage(): void {
    if (this.canDecrement()) {
      this.pageNumber--;
      this.updatePagination();
    }
  }

  async loadMoreMonths(): Promise<void> {
    this.monthsLoaded += this.defaultMonths;
    await this.refresh();
  }

  private shiftMonths(base: Date, deltaMonths: number): Date {
    const copy = new Date(base);
    copy.setHours(0, 0, 0, 0);
    const day = copy.getDate();
    copy.setMonth(copy.getMonth() + deltaMonths, 1);
    const lastDay = new Date(copy.getFullYear(), copy.getMonth() + 1, 0).getDate();
    copy.setDate(Math.min(day, lastDay));
    return copy;
  }

  private getCutoffDate(): Date {
    const today = new Date();
    return this.shiftMonths(today, -this.monthsLoaded);
  }

}
